/**
 * Data hook for finding distributor users to add to a chat.
 *
 * Default (scopeToSupplier=false): a global `isSupplier:true` user search —
 * the operator types a term and we search across all distributor users. This
 * is the primary flow.
 *
 * Opt-in (scopeToSupplier=true): list only THIS chat's distributor's users
 * via `supplier(id).users` (mirrors the buyer side). Toggled from the modal
 * when the operator wants to narrow to the distributor's own team. The list is
 * paged in full up front so the modal can filter it locally.
 */

import type {
  UserSelectOption,
  UserMetadata,
  OrderPreference,
  OrderPreferenceType,
} from '@choco-admin/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSearchUsersForChatLazyQuery } from '../../../generated';
import { usePuppyMode } from '../../../hooks';

export interface UseSearchSupplierUsersConfig {
  /** Distributor ENTITY id (used only when scopeToSupplier is true). */
  supplierId: string;
  /** When true, list only this distributor's users instead of a global search. */
  scopeToSupplier?: boolean;
}

export interface UseSearchSupplierUsersResponse {
  users: UserSelectOption[];
  isLoading: boolean;
  error: Error | null;
  searchUsers: (searchTerm: string) => Promise<void>;
  clearResults: () => void;
}

/**
 * How many users one request asks the search index for.
 *
 * The scoped list searches for the distributor's entity id, which the index
 * matches against a user's linked supplier, then pages by offset until it has the
 * `total` the index reported. Offset paging is used rather than
 * `supplier(id).users`: that connection resolves each page through a
 * `BatchGetItem` and emits the edges in whatever order the batch came back
 * (`userService/src/handlers/supplierUsers.ts`), so its last edge is not the end of
 * the index window and resuming from it re-reads rows already seen. There is no
 * `pageInfo` to ask for the real boundary either. An offset does not depend on the
 * previous page at all.
 */
const SCOPED_PAGE_SIZE = 100;

/**
 * Where the index stops paging.
 *
 * `size + offset > 15000` makes the backend silently rewrite the request to
 * `offset: 0, size: 15000` (`indexerService/src/queryDocument/index.ts`), so a loop
 * that keeps asking gets the first page again and counts it twice. Stop at the
 * window instead: no distributor is near it, and reading the same page twice is
 * worse than stopping short of a number nobody reaches.
 */
const SEARCH_WINDOW = 15000;

const buildOrderPreferences = (
  orderPreference:
    | { whatsApp?: string | null; sms?: string | null; fax?: string | null }
    | null
    | undefined
): OrderPreference[] => {
  const prefs: OrderPreference[] = [];
  if (orderPreference?.whatsApp) {
    prefs.push({
      type: 'WHATSAPP' as OrderPreferenceType,
      value: orderPreference.whatsApp,
    });
  }
  if (orderPreference?.sms) {
    prefs.push({
      type: 'SMS' as OrderPreferenceType,
      value: orderPreference.sms,
    });
  }
  if (orderPreference?.fax) {
    prefs.push({
      type: 'FAX' as OrderPreferenceType,
      value: orderPreference.fax,
    });
  }
  return prefs;
};

export const useSearchSupplierUsers = (
  config: UseSearchSupplierUsersConfig
): UseSearchSupplierUsersResponse => {
  const { supplierId, scopeToSupplier = false } = config;
  // Follow the global Puppy/Dogfood toggle: undefined = show all users,
  // false = hide dogfood accounts, true = only dogfood accounts.
  const { puppyFilter } = usePuppyMode();

  // Global search (default): type-to-search across all distributor users.
  const [executeGlobalSearch, globalResult] = useSearchUsersForChatLazyQuery({
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  // Scoped list (opt-in): this distributor's own users, preloaded. The same search
  // the global mode uses, with the distributor's entity id as the term.
  const [executeScopedSearch] = useSearchUsersForChatLazyQuery({
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const [scopedUsers, setScopedUsers] = useState<UserSelectOption[]>([]);
  const [scopedLoading, setScopedLoading] = useState(false);
  const [scopedError, setScopedError] = useState<Error | null>(null);
  const scopedRequestRef = useRef(0);

  const loadScopedUsers = useCallback(async () => {
    scopedRequestRef.current += 1;
    // A load started for an earlier distributor must never overwrite a newer
    // one — landing the wrong team here would add the wrong user to the chat.
    const requestId = scopedRequestRef.current;
    const isStale = () => scopedRequestRef.current !== requestId;

    setScopedLoading(true);
    setScopedError(null);

    // Offset paging, bounded by the `total` the index reports. Each request is
    // independent of the last, so a page cannot land in the wrong place.
    const collectPage = async (
      offset: number,
      collected: UserSelectOption[]
    ): Promise<UserSelectOption[]> => {
      const result = await executeScopedSearch({
        variables: {
          search: supplierId,
          limit: SCOPED_PAGE_SIZE,
          offset,
          filter: {
            user: {
              isSupplier: true,
              ...(puppyFilter !== undefined ? { isPuppy: puppyFilter } : {}),
            },
          },
        },
      });

      if (result.error) throw result.error;

      const total = result.data?.search?.total ?? 0;
      const hits = result.data?.search?.hits ?? [];

      hits.forEach((hit) => {
        const node = hit?.entity;
        if (!node || node.__typename !== 'User') return;
        // The bot is in the chat by construction, so it must never be offered as
        // a selectable teammate. The index does not return it today, which is not
        // a guarantee to lean on.
        if (node.botUser) return;
        // The term is an id, so a hit that is not linked to this distributor would
        // mean the index matched something else. Belt and braces, and it keeps a
        // future scoring change from quietly widening the list.
        if (node.linkedSupplier && node.linkedSupplier.id !== supplierId)
          return;
        collected.push({
          value: node.id,
          label: node.name ?? node.phone ?? node.id,
          metaData: {
            name: node.name ?? '',
            supplierId,
            phone: node.phone ?? '',
            email: node.email ?? '',
            orderPreferences: buildOrderPreferences(node.orderPreference),
            supplier: true,
            referenceId: null,
          },
        });
      });

      const nextOffset = offset + hits.length;
      // `hits.length` guards the case where the index reports a total it will not
      // serve: without it a short page would loop forever. The window guard stops
      // the request that would come back rewritten to offset 0.
      if (
        hits.length === 0 ||
        nextOffset >= total ||
        nextOffset + SCOPED_PAGE_SIZE > SEARCH_WINDOW
      ) {
        return collected;
      }

      return collectPage(nextOffset, collected);
    };

    try {
      const collected = await collectPage(0, []);
      if (isStale()) return;
      setScopedUsers(collected);
    } catch (loadError) {
      if (isStale()) return;
      setScopedUsers([]);
      setScopedError(
        loadError instanceof Error
          ? loadError
          : new Error('Could not load this distributor’s users.')
      );
    } finally {
      if (!isStale()) setScopedLoading(false);
    }
  }, [executeScopedSearch, supplierId, puppyFilter]);

  // Preload the distributor's users when the scoped mode is enabled.
  useEffect(() => {
    if (!scopeToSupplier || !supplierId) return;
    loadScopedUsers();
  }, [scopeToSupplier, supplierId, loadScopedUsers]);

  const searchUsers = useCallback(
    async (searchTerm: string) => {
      // The scoped list is already loaded in full; the modal filters it locally.
      if (scopeToSupplier) return;

      await executeGlobalSearch({
        variables: {
          search: searchTerm.trim(),
          limit: 20,
          filter: {
            user: {
              isSupplier: true,
              ...(puppyFilter !== undefined ? { isPuppy: puppyFilter } : {}),
            },
          },
        },
      });
    },
    [scopeToSupplier, puppyFilter, executeGlobalSearch]
  );

  const clearResults = useCallback(() => {
    if (scopeToSupplier) return;
    executeGlobalSearch({
      variables: {
        search: '',
        limit: 0,
        filter: {
          user: {
            isSupplier: true,
            ...(puppyFilter !== undefined ? { isPuppy: puppyFilter } : {}),
          },
        },
      },
    });
  }, [scopeToSupplier, puppyFilter, executeGlobalSearch]);

  const users = useMemo((): UserSelectOption[] => {
    if (scopeToSupplier) return scopedUsers;

    const hits = globalResult.data?.search?.hits;
    if (!hits) return [];
    return hits.reduce<UserSelectOption[]>((acc, hit) => {
      const { entity } = hit;
      if (entity?.__typename !== 'User') return acc;
      // Exclude bot accounts — the supplier bot is already in the chat by
      // construction; the add-user dropdown should list human suppliers only.
      if (entity.botUser) return acc;
      if (!entity.supplier) return acc;

      const metaData: UserMetadata = {
        name: entity.name,
        supplierId,
        phone: entity.phone,
        email: entity.email,
        orderPreferences: buildOrderPreferences(entity.orderPreference),
        supplier: true,
        referenceId: null,
      };
      acc.push({
        value: entity.id,
        label: entity.name ?? entity.phone,
        metaData,
      });
      return acc;
    }, []);
  }, [scopeToSupplier, scopedUsers, globalResult.data, supplierId]);

  return {
    users,
    isLoading: scopeToSupplier ? scopedLoading : globalResult.loading,
    error: (scopeToSupplier ? scopedError : globalResult.error) ?? null,
    searchUsers,
    clearResults,
  };
};
